import { useState, useEffect } from 'react';
import {
  StickyNote, CalendarDays, BarChart3, Plus, Trash2, Pin, PinOff,
  Search, X, Check, Flame, Clock, BookOpen, Edit3,
  Save, ArrowLeft, TrendingUp, Target, Calendar, Smile,
  Meh, Frown, Star, ChevronLeft, ChevronRight, NotebookPen,
  Sparkles,
} from 'lucide-react';
import prepApi from '../api/prep';
import { useAuth } from '../hooks/useAuth';

/* ── constants ─────────────────────────────────────────────────────────── */
const TABS = [
  { id: 'notes',     label: 'Notes',     Icon: StickyNote   },
  { id: 'timetable', label: 'Timetable', Icon: CalendarDays },
  { id: 'tracker',   label: 'Tracker',   Icon: BarChart3    },
];

const DAYS     = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_FULL = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };

const CATEGORIES = [
  { id: 'general',   label: 'General',     color: '#6B7280' },
  { id: 'notes',     label: 'Study Notes', color: '#6366F1' },
  { id: 'formulas',  label: 'Formulas',    color: '#10B981' },
  { id: 'flashcard', label: 'Flashcard',   color: '#F59E0B' },
  { id: 'important', label: 'Important',   color: '#EF4444' },
  { id: 'revision',  label: 'Revision',    color: '#8B5CF6' },
  { id: 'practice',  label: 'Practice',    color: '#3B82F6' },
];

const NOTE_COLORS = [
  '#6366F1', '#8B5CF6', '#EC4899', '#EF4444', '#F97316',
  '#F59E0B', '#10B981', '#14B8A6', '#3B82F6', '#6B7280',
];

const SUBJECTS = [
  'Mathematics', 'Physics', 'Chemistry', 'Biology', 'English',
  'History', 'Geography', 'Economics', 'Computer Science',
  'Science', 'Languages', 'Revision', 'Mock Test', 'Other',
];

const SUBJECT_COLORS = {
  'Mathematics':    '#6366F1',
  'Physics':        '#3B82F6',
  'Chemistry':      '#10B981',
  'Biology':        '#22C55E',
  'English':        '#F59E0B',
  'History':        '#F97316',
  'Geography':      '#14B8A6',
  'Economics':      '#8B5CF6',
  'Computer Science': '#EC4899',
  'Science':        '#10B981',
  'Languages':      '#EF4444',
  'Revision':       '#6B7280',
  'Mock Test':      '#DC2626',
  'Other':          '#94A3B8',
};

const MOODS = [
  { id: 'great', label: 'Great', Icon: Star,  color: '#10B981' },
  { id: 'good',  label: 'Good',  Icon: Smile, color: '#6366F1' },
  { id: 'okay',  label: 'Okay',  Icon: Meh,   color: '#F59E0B' },
  { id: 'poor',  label: 'Poor',  Icon: Frown, color: '#EF4444' },
];

const cat      = id => CATEGORIES.find(c => c.id === id) || CATEGORIES[0];
const todayStr = () => new Date().toISOString().slice(0, 10);

/* ── helpers ───────────────────────────────────────────────────────────── */
function Badge({ label, color }) {
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ background: color + '22', color }}>
      {label}
    </span>
  );
}

function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-slate-100 rounded-2xl ${className}`} />;
}

/* ════════════════════════════════════════════════════════════════════════
   NOTES TAB
   ════════════════════════════════════════════════════════════════════════ */
function NotesTab() {
  const [notes, setNotes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ title: '', content: '', category: 'general', tags: [], color: '#6366F1', isPinned: false });
  const [saving, setSaving]       = useState(false);
  const [tagInput, setTagInput]   = useState('');

  useEffect(() => {
    prepApi.getNotes().then(setNotes).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = notes.filter(n => {
    if (catFilter !== 'all' && n.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!n.title?.toLowerCase().includes(q) && !n.content?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const pinned   = filtered.filter(n => n.isPinned);
  const unpinned = filtered.filter(n => !n.isPinned);

  function openNew() {
    setForm({ title: '', content: '', category: 'general', tags: [], color: '#6366F1', isPinned: false });
    setTagInput('');
    setEditing('new');
  }

  function openEdit(note) {
    setForm({ title: note.title, content: note.content, category: note.category, tags: note.gsTags || [], color: note.color || '#6366F1', isPinned: note.isPinned });
    setTagInput('');
    setEditing(note);
  }

  async function save() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, gsTags: form.tags };
      if (editing === 'new') {
        const n = await prepApi.createNote(payload);
        setNotes(prev => [n, ...prev]);
      } else {
        const n = await prepApi.updateNote(editing._id, payload);
        setNotes(prev => prev.map(x => x._id === n._id ? n : x));
      }
      setEditing(null);
    } finally { setSaving(false); }
  }

  async function togglePin(note) {
    const updated = await prepApi.updateNote(note._id, { isPinned: !note.isPinned });
    setNotes(prev => prev.map(n => n._id === updated._id ? updated : n));
  }

  async function del(note) {
    await prepApi.deleteNote(note._id);
    setNotes(prev => prev.filter(n => n._id !== note._id));
  }

  function addTag(e) {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const t = tagInput.trim().replace(/,$/, '');
      if (t && !form.tags.includes(t)) setForm(f => ({ ...f, tags: [...f.tags, t] }));
      setTagInput('');
    }
  }

  function removeTag(tag) {
    setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }));
  }

  /* ── editor ── */
  if (editing !== null) {
    return (
      <div className="flex flex-col gap-4 pb-8">
        <button onClick={() => setEditing(null)}
          className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm bg-transparent border-none cursor-pointer p-0 w-fit">
          <ArrowLeft size={15} /> Back to Notes
        </button>

        {/* accent colors */}
        <div className="flex items-center gap-2 flex-wrap">
          {NOTE_COLORS.map(col => (
            <button key={col} onClick={() => setForm(f => ({ ...f, color: col }))}
              className="w-7 h-7 rounded-full cursor-pointer transition-all hover:scale-110 border-0 flex items-center justify-center"
              style={{ background: col, outline: form.color === col ? `3px solid ${col}` : 'none', outlineOffset: 2 }} />
          ))}
          <button onClick={() => setForm(f => ({ ...f, isPinned: !f.isPinned }))}
            className="ml-auto flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-all"
            style={form.isPinned
              ? { background: '#FFFBEB', borderColor: '#F59E0B', color: '#D97706' }
              : { background: '#F8FAFC', borderColor: '#E2E8F0', color: '#94A3B8' }}>
            <Pin size={11} /> {form.isPinned ? 'Pinned' : 'Pin it'}
          </button>
        </div>

        {/* title */}
        <input
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Note title…"
          className="w-full text-xl font-bold text-slate-800 border-0 border-b-2 pb-2.5 outline-none bg-transparent placeholder:text-slate-300"
          style={{ borderColor: form.color }}
        />

        {/* category chips */}
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Category</p>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setForm(f => ({ ...f, category: c.id }))}
                className="text-xs px-3 py-1 rounded-full border cursor-pointer transition-all font-medium"
                style={form.category === c.id
                  ? { background: c.color, borderColor: c.color, color: '#fff' }
                  : { background: '#F8FAFC', borderColor: '#E2E8F0', color: '#64748B' }}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* tags */}
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Tags</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {form.tags.map(tag => (
              <span key={tag} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: form.color + '20', color: form.color }}>
                #{tag}
                <button onClick={() => removeTag(tag)} className="cursor-pointer border-none bg-transparent p-0 leading-none">
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
          <input
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={addTag}
            placeholder="Type a tag and press Enter…"
            className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 outline-none bg-slate-50 text-slate-700 placeholder:text-slate-300"
          />
        </div>

        {/* content */}
        <textarea
          value={form.content}
          onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
          placeholder="Write your notes here…"
          rows={12}
          className="w-full text-sm text-slate-700 leading-relaxed border border-slate-200 rounded-2xl p-4 outline-none resize-none bg-slate-50 focus:border-indigo-200"
        />

        <button onClick={save} disabled={saving || !form.title.trim()}
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-white font-bold cursor-pointer border-none transition-opacity disabled:opacity-40 text-sm"
          style={{ background: `linear-gradient(135deg, ${form.color}, ${form.color}cc)` }}>
          <Save size={15} /> {saving ? 'Saving…' : 'Save Note'}
        </button>
      </div>
    );
  }

  /* ── list ── */
  return (
    <div className="flex flex-col gap-4 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-base text-slate-800">My Notes</h2>
          <p className="text-xs text-slate-400 mt-0.5">{notes.length} note{notes.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl text-white text-sm font-bold cursor-pointer border-none shadow-sm"
          style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}>
          <Plus size={15} /> New Note
        </button>
      </div>

      {/* search */}
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-sm">
        <Search size={14} className="text-slate-300 shrink-0" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search notes…"
          className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder:text-slate-300" />
        {search && <button onClick={() => setSearch('')} className="cursor-pointer border-none bg-transparent p-0"><X size={12} className="text-slate-300" /></button>}
      </div>

      {/* category filter */}
      <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
        {[{ id: 'all', label: 'All', color: '#1e293b' }, ...CATEGORIES].map(c => (
          <button key={c.id} onClick={() => setCatFilter(c.id)}
            className="shrink-0 text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-all font-medium"
            style={catFilter === c.id
              ? { background: c.color, borderColor: c.color, color: '#fff' }
              : { background: '#fff', borderColor: '#E2E8F0', color: '#94A3B8' }}>
            {c.label}
          </button>
        ))}
      </div>

      {loading && <div className="flex flex-col gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-28" />)}</div>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 text-slate-300">
          <StickyNote size={44} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold text-slate-400">No notes yet</p>
          <p className="text-sm mt-1">Start by creating your first note</p>
        </div>
      )}

      {pinned.length > 0 && (
        <div>
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">📌 Pinned</p>
          <div className="flex flex-col gap-3">
            {pinned.map(n => <NoteCard key={n._id} note={n} onEdit={openEdit} onPin={togglePin} onDelete={del} />)}
          </div>
        </div>
      )}

      {unpinned.length > 0 && (
        <div>
          {pinned.length > 0 && <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 mt-3">All Notes</p>}
          <div className="flex flex-col gap-3">
            {unpinned.map(n => <NoteCard key={n._id} note={n} onEdit={openEdit} onPin={togglePin} onDelete={del} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function NoteCard({ note, onEdit, onPin, onDelete }) {
  const c = cat(note.category);
  const color = note.color || c.color;
  return (
    <div className="rounded-2xl bg-white shadow-sm overflow-hidden border border-slate-100 hover:shadow-md transition-shadow">
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${color}80)` }} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="font-bold text-sm text-slate-800 leading-snug flex-1 line-clamp-2">{note.title}</p>
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={() => onPin(note)} className="p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer border-none bg-transparent">
              {note.isPinned ? <Pin size={13} className="text-amber-500" /> : <PinOff size={13} className="text-slate-300" />}
            </button>
            <button onClick={() => onEdit(note)} className="p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer border-none bg-transparent">
              <Edit3 size={13} className="text-slate-300" />
            </button>
            <button onClick={() => onDelete(note)} className="p-1.5 rounded-xl hover:bg-red-50 cursor-pointer border-none bg-transparent">
              <Trash2 size={13} className="text-slate-300 hover:text-red-400" />
            </button>
          </div>
        </div>
        {note.content && (
          <p className="text-xs text-slate-500 mt-2 line-clamp-3 leading-relaxed">{note.content}</p>
        )}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <Badge label={c.label} color={color} />
          {(note.gsTags || []).slice(0, 3).map(t => (
            <span key={t} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
              style={{ background: color + '15', color }}>#{t}</span>
          ))}
          <span className="ml-auto text-[10px] text-slate-300">
            {new Date(note.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   TIMETABLE TAB
   ════════════════════════════════════════════════════════════════════════ */
function TimetableTab() {
  const [slots, setSlots]         = useState([]);
  const [targetHours, setTarget]  = useState(8);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [addingDay, setAddingDay] = useState(null);
  const [saved, setSaved]         = useState(false);
  const [newSlot, setNewSlot]     = useState({ startTime: '09:00', endTime: '11:00', subject: 'Mathematics', task: '' });

  useEffect(() => {
    prepApi.getTimetable()
      .then(d => { setSlots(d.slots || []); setTarget(d.dailyTargetHours || 8); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      await prepApi.saveTimetable({ slots, dailyTargetHours: targetHours });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  }

  function addSlot() {
    if (!addingDay) return;
    const color = SUBJECT_COLORS[newSlot.subject] || '#6366F1';
    setSlots(prev => [...prev, { ...newSlot, day: addingDay, color, _id: Date.now().toString() }]);
    setAddingDay(null);
    setNewSlot({ startTime: '09:00', endTime: '11:00', subject: 'Mathematics', task: '' });
  }

  function removeSlot(id) { setSlots(prev => prev.filter(s => s._id !== id)); }

  function slotDuration(start, end) {
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return Math.max(0, (eh * 60 + em) - (sh * 60 + sm)) / 60;
  }

  function totalDayHours(day) {
    return slots.filter(s => s.day === day).reduce((sum, s) => sum + slotDuration(s.startTime, s.endTime), 0);
  }

  if (loading) return <div className="flex flex-col gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div>;

  return (
    <div className="flex flex-col gap-5 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-base text-slate-800">Weekly Timetable</h2>
          <p className="text-xs text-slate-400 mt-0.5">Plan your study sessions</p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl text-white text-sm font-bold cursor-pointer border-none shadow-sm transition-all disabled:opacity-60"
          style={{ background: saved ? 'linear-gradient(135deg,#10B981,#059669)' : 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}>
          {saved ? <><Check size={14} /> Saved!</> : <><Save size={14} /> {saving ? 'Saving…' : 'Save'}</>}
        </button>
      </div>

      {/* daily target */}
      <div className="flex items-center gap-3 rounded-2xl p-4 border"
        style={{ background: 'linear-gradient(135deg,#EEF2FF,#F5F3FF)', borderColor: '#C7D2FE' }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#6366F120' }}>
          <Target size={18} className="text-indigo-500" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-700">Daily Study Goal</p>
          <p className="text-xs text-slate-400">Hours per day target</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTarget(h => Math.max(1, h - 0.5))}
            className="w-8 h-8 rounded-xl bg-white text-indigo-600 font-bold cursor-pointer border border-indigo-100 flex items-center justify-center shadow-sm text-lg leading-none">−</button>
          <span className="text-lg font-extrabold text-indigo-700 w-10 text-center">{targetHours}h</span>
          <button onClick={() => setTarget(h => Math.min(18, h + 0.5))}
            className="w-8 h-8 rounded-xl bg-white text-indigo-600 font-bold cursor-pointer border border-indigo-100 flex items-center justify-center shadow-sm text-lg leading-none">+</button>
        </div>
      </div>

      {/* days */}
      {DAYS.map(day => {
        const daySlots = slots.filter(s => s.day === day).sort((a, b) => a.startTime.localeCompare(b.startTime));
        const totalH = totalDayHours(day);
        const pct    = Math.min(100, (totalH / targetHours) * 100);
        const done   = pct >= 100;
        return (
          <div key={day} className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-extrabold"
                  style={{ background: done ? '#D1FAE5' : '#EEF2FF', color: done ? '#059669' : '#6366F1' }}>
                  {day.slice(0, 1)}
                </div>
                <div>
                  <p className="font-bold text-sm text-slate-800">{DAY_FULL[day]}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: done ? '#10B981' : '#6366F1' }} />
                    </div>
                    <span className="text-[10px] text-slate-400">{totalH.toFixed(1)}h / {targetHours}h</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setAddingDay(day)}
                className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-xl cursor-pointer border-none transition-colors"
                style={{ background: '#EEF2FF', color: '#6366F1' }}>
                <Plus size={12} /> Add
              </button>
            </div>

            <div className="divide-y divide-slate-50">
              {daySlots.length === 0 && (
                <p className="text-xs text-slate-300 text-center py-4">No sessions yet — click Add</p>
              )}
              {daySlots.map(slot => (
                <div key={slot._id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-1 h-10 rounded-full shrink-0" style={{ background: slot.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800">{slot.subject}</p>
                    <p className="text-xs text-slate-400">{slot.startTime} – {slot.endTime}
                      <span className="ml-1.5 text-slate-300">· {slotDuration(slot.startTime, slot.endTime).toFixed(1)}h</span>
                    </p>
                    {slot.task && <p className="text-xs text-slate-400 mt-0.5 truncate">{slot.task}</p>}
                  </div>
                  <button onClick={() => removeSlot(slot._id)} className="p-1.5 rounded-xl hover:bg-red-50 cursor-pointer border-none bg-transparent">
                    <Trash2 size={13} className="text-slate-200 hover:text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* add slot sheet */}
      {addingDay && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-t-3xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-extrabold text-base text-slate-800">Add Session — {DAY_FULL[addingDay]}</h3>
              <button onClick={() => setAddingDay(null)} className="p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer border-none bg-transparent">
                <X size={16} className="text-slate-400" />
              </button>
            </div>
            <div className="flex flex-col gap-3.5">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">Subject</label>
                <select value={newSlot.subject} onChange={e => setNewSlot(s => ({ ...s, subject: e.target.value }))}
                  className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-700 outline-none bg-slate-50">
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">Start</label>
                  <input type="time" value={newSlot.startTime} onChange={e => setNewSlot(s => ({ ...s, startTime: e.target.value }))}
                    className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-700 outline-none bg-slate-50" />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">End</label>
                  <input type="time" value={newSlot.endTime} onChange={e => setNewSlot(s => ({ ...s, endTime: e.target.value }))}
                    className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-700 outline-none bg-slate-50" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">Task / Topic</label>
                <input value={newSlot.task} onChange={e => setNewSlot(s => ({ ...s, task: e.target.value }))}
                  placeholder="e.g. Chapter 3 — Quadratic Equations"
                  className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-700 outline-none bg-slate-50" />
              </div>
              <button onClick={addSlot}
                className="w-full py-3.5 rounded-2xl text-white font-bold cursor-pointer border-none mt-1 text-sm"
                style={{ background: `linear-gradient(135deg, ${SUBJECT_COLORS[newSlot.subject] || '#6366F1'}, ${SUBJECT_COLORS[newSlot.subject] || '#6366F1'}aa)` }}>
                Add Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   TRACKER TAB
   ════════════════════════════════════════════════════════════════════════ */
function TrackerTab({ logs, setLogs, streak, setStreak, loading }) {
  const [logDate, setLogDate]       = useState(todayStr());
  const [editing, setEditing]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [form, setForm]             = useState({ sessions: [], mood: 'good', dailyReview: '' });

  useEffect(() => {
    const existing = logs.find(l => l.dateStr === logDate);
    if (existing) {
      setForm({ sessions: existing.sessions || [], mood: existing.mood || 'good', dailyReview: existing.dailyReview || '' });
    } else {
      setForm({ sessions: [], mood: 'good', dailyReview: '' });
    }
  }, [logDate, logs]);

  function addSession() {
    setForm(f => ({ ...f, sessions: [...f.sessions, { subject: 'Mathematics', hours: 1, topicsCovered: '', notes: '' }] }));
  }

  function updateSession(i, field, value) {
    setForm(f => {
      const sessions = [...f.sessions];
      sessions[i] = { ...sessions[i], [field]: field === 'hours' ? +value : value };
      return { ...f, sessions };
    });
  }

  function removeSession(i) {
    setForm(f => ({ ...f, sessions: f.sessions.filter((_, idx) => idx !== i) }));
  }

  async function saveLog() {
    setSaving(true);
    try {
      const log = await prepApi.saveLog({ dateStr: logDate, ...form });
      setLogs(prev => {
        const idx = prev.findIndex(l => l.dateStr === logDate);
        if (idx >= 0) { const a = [...prev]; a[idx] = log; return a; }
        return [log, ...prev];
      });
      const s = await prepApi.getStreak();
      setStreak(s);
      setEditing(false);
    } finally { setSaving(false); }
  }

  function getWeekDays() {
    const days = [];
    const base = new Date();
    base.setDate(base.getDate() + weekOffset * 7);
    const dow    = base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - ((dow + 6) % 7));
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday); d.setDate(monday.getDate() + i);
      days.push(d.toISOString().slice(0, 10));
    }
    return days;
  }

  const weekDays  = getWeekDays();
  const weekLog   = d => logs.find(l => l.dateStr === d);
  const weekTotal = weekDays.reduce((s, d) => s + (weekLog(d)?.totalHours || 0), 0);
  const existingLog = logs.find(l => l.dateStr === logDate);
  const dayTotal    = form.sessions.reduce((s, sess) => s + (Number(sess.hours) || 0), 0);
  const moodObj     = id => MOODS.find(m => m.id === id) || MOODS[1];

  if (loading) return <div className="flex flex-col gap-3">{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div>;

  return (
    <div className="flex flex-col gap-5 pb-8">
      <div>
        <h2 className="font-bold text-base text-slate-800">Study Tracker</h2>
        <p className="text-xs text-slate-400 mt-0.5">Track your daily progress</p>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Day Streak',  value: streak.streak,                          unit: 'd', Icon: Flame,     from: '#FD7E14', to: '#FB923C', bg: '#FFF7ED', border: '#FED7AA' },
          { label: 'Avg / Day',   value: Number(streak.avgHours).toFixed(1),     unit: 'h', Icon: Clock,     from: '#6366F1', to: '#8B5CF6', bg: '#EEF2FF', border: '#C7D2FE' },
          { label: 'Total Hours', value: Number(streak.totalHours).toFixed(1),   unit: 'h', Icon: TrendingUp, from: '#10B981', to: '#34D399', bg: '#ECFDF5', border: '#A7F3D0' },
          { label: 'Days Logged', value: streak.totalDays,                       unit: '',  Icon: BookOpen,  from: '#8B5CF6', to: '#A78BFA', bg: '#F5F3FF', border: '#DDD6FE' },
        ].map(({ label, value, unit, Icon, from, to, bg, border }) => (
          <div key={label} className="rounded-2xl p-4 border flex items-center gap-3" style={{ background: bg, borderColor: border }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `linear-gradient(135deg,${from},${to})` }}>
              <Icon size={18} color="#fff" />
            </div>
            <div>
              <p className="text-2xl font-extrabold" style={{ color: from }}>{value}{unit}</p>
              <p className="text-xs font-medium" style={{ color: from + 'aa' }}>{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* week strip */}
      <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="font-bold text-sm text-slate-700">
            {weekOffset === 0 ? 'This Week' : weekOffset === -1 ? 'Last Week' : `${weekOffset < 0 ? Math.abs(weekOffset) + ' weeks ago' : weekOffset + ' weeks ahead'}`}
            <span className="text-slate-400 font-medium ml-1.5">· {weekTotal.toFixed(1)}h</span>
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => setWeekOffset(o => o - 1)} className="p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer border-none bg-transparent">
              <ChevronLeft size={15} className="text-slate-400" />
            </button>
            {weekOffset !== 0 && (
              <button onClick={() => setWeekOffset(0)} className="text-xs text-indigo-500 font-bold px-2 py-1 rounded-xl hover:bg-indigo-50 cursor-pointer border-none bg-transparent">
                Today
              </button>
            )}
            <button onClick={() => setWeekOffset(o => o + 1)} className="p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer border-none bg-transparent">
              <ChevronRight size={15} className="text-slate-400" />
            </button>
          </div>
        </div>
        <div className="flex gap-1">
          {weekDays.map(d => {
            const log    = weekLog(d);
            const hours  = log?.totalHours || 0;
            const isToday = d === todayStr();
            const isSel   = d === logDate;
            const dayLabel = new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short' });
            const dayNum   = new Date(d + 'T00:00:00').getDate();
            const pct = Math.min(100, (hours / 8) * 100);
            return (
              <button key={d} onClick={() => setLogDate(d)}
                className="flex-1 flex flex-col items-center gap-1.5 py-2.5 rounded-2xl cursor-pointer border-2 transition-all"
                style={{
                  background: isSel ? '#EEF2FF' : 'transparent',
                  borderColor: isSel ? '#6366F1' : 'transparent',
                }}>
                <span className="text-[10px] font-medium text-slate-400">{dayLabel}</span>
                <div className="w-8 h-8 rounded-full flex items-center justify-center relative"
                  style={{ background: hours > 0 ? 'linear-gradient(135deg,#6366F1,#8B5CF6)' : '#F1F5F9' }}>
                  {isToday && <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-orange-400 border-2 border-white" />}
                  <span className="text-xs font-extrabold" style={{ color: hours > 0 ? '#fff' : '#CBD5E1' }}>{dayNum}</span>
                </div>
                <span className="text-[9px] font-bold" style={{ color: hours > 0 ? '#6366F1' : '#E2E8F0' }}>
                  {hours > 0 ? `${hours.toFixed(1)}h` : '·'}
                </span>
                <div className="w-full px-1">
                  <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#6366F1,#8B5CF6)' }} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* day log */}
      <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-50 bg-slate-50/60">
          <div>
            <p className="font-bold text-sm text-slate-800">
              {new Date(logDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            {existingLog && (
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs font-bold text-indigo-500">{existingLog.totalHours.toFixed(1)}h logged</span>
                {(() => { const m = moodObj(existingLog.mood); return <Badge label={m.label} color={m.color} />; })()}
              </div>
            )}
          </div>
          {!editing ? (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-xl cursor-pointer border-none"
              style={{ background: '#EEF2FF', color: '#6366F1' }}>
              <Edit3 size={12} /> {existingLog ? 'Edit' : 'Log Day'}
            </button>
          ) : (
            <button onClick={() => setEditing(false)}
              className="text-xs text-slate-400 font-medium px-3 py-1.5 rounded-xl hover:bg-slate-100 cursor-pointer border-none bg-transparent">
              Cancel
            </button>
          )}
        </div>

        {!editing && existingLog && (
          <div className="divide-y divide-slate-50">
            {existingLog.sessions.map((s, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-3">
                <div className="w-1 h-10 rounded-full shrink-0 mt-0.5" style={{ background: SUBJECT_COLORS[s.subject] || '#6366F1' }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-slate-800">{s.subject}</p>
                    <Badge label={`${s.hours}h`} color={SUBJECT_COLORS[s.subject] || '#6366F1'} />
                  </div>
                  {s.topicsCovered && <p className="text-xs text-slate-500 mt-0.5 truncate">{s.topicsCovered}</p>}
                  {s.notes && <p className="text-xs text-slate-400 mt-0.5 line-clamp-2 italic">{s.notes}</p>}
                </div>
              </div>
            ))}
            {existingLog.dailyReview && (
              <div className="px-4 py-3 bg-slate-50/50">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">Day Review</p>
                <p className="text-xs text-slate-600 leading-relaxed">{existingLog.dailyReview}</p>
              </div>
            )}
          </div>
        )}

        {!editing && !existingLog && (
          <div className="text-center py-10 text-slate-300">
            <Calendar size={36} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm text-slate-400 font-medium">Nothing logged yet</p>
            <p className="text-xs mt-1 text-slate-300">Tap "Log Day" to record your study session</p>
          </div>
        )}

        {editing && (
          <div className="p-4 flex flex-col gap-4">
            {/* mood */}
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">How did it go?</p>
              <div className="flex gap-2">
                {MOODS.map(m => {
                  const Icon = m.Icon;
                  return (
                    <button key={m.id} onClick={() => setForm(f => ({ ...f, mood: m.id }))}
                      className="flex-1 flex flex-col items-center gap-1 py-2.5 rounded-2xl border-2 cursor-pointer transition-all"
                      style={form.mood === m.id
                        ? { borderColor: m.color, background: m.color + '18' }
                        : { borderColor: '#F1F5F9', background: 'transparent' }}>
                      <Icon size={20} style={{ color: m.color }} />
                      <span className="text-[10px] font-bold" style={{ color: m.color }}>{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* sessions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">Sessions — {dayTotal.toFixed(1)}h</p>
                <button onClick={addSession}
                  className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-xl cursor-pointer border-none"
                  style={{ background: '#EEF2FF', color: '#6366F1' }}>
                  <Plus size={11} /> Add
                </button>
              </div>
              <div className="flex flex-col gap-3">
                {form.sessions.length === 0 && (
                  <p className="text-xs text-slate-300 text-center py-4">Add at least one study session</p>
                )}
                {form.sessions.map((sess, i) => (
                  <div key={i} className="rounded-2xl border border-slate-100 p-3 flex flex-col gap-2 bg-slate-50/50">
                    <div className="flex gap-2">
                      <select value={sess.subject} onChange={e => updateSession(i, 'subject', e.target.value)}
                        className="flex-1 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-700 outline-none bg-white">
                        {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <div className="flex items-center gap-1 border border-slate-200 rounded-xl px-2 py-1 bg-white">
                        <button onClick={() => updateSession(i, 'hours', Math.max(0.25, sess.hours - 0.25).toFixed(2))}
                          className="text-slate-400 font-bold cursor-pointer border-none bg-transparent text-base leading-none w-5 text-center">−</button>
                        <span className="text-xs font-extrabold text-slate-700 w-8 text-center">{Number(sess.hours).toFixed(1)}h</span>
                        <button onClick={() => updateSession(i, 'hours', Math.min(12, sess.hours + 0.25).toFixed(2))}
                          className="text-slate-400 font-bold cursor-pointer border-none bg-transparent text-base leading-none w-5 text-center">+</button>
                      </div>
                      <button onClick={() => removeSession(i)} className="p-1.5 rounded-xl hover:bg-red-50 cursor-pointer border-none bg-transparent">
                        <Trash2 size={13} className="text-slate-300 hover:text-red-400" />
                      </button>
                    </div>
                    <input value={sess.topicsCovered} onChange={e => updateSession(i, 'topicsCovered', e.target.value)}
                      placeholder="Topics covered…"
                      className="w-full text-xs border border-slate-100 rounded-xl px-3 py-2 outline-none bg-white text-slate-600 placeholder:text-slate-300" />
                    <input value={sess.notes} onChange={e => updateSession(i, 'notes', e.target.value)}
                      placeholder="Notes / observations…"
                      className="w-full text-xs border border-slate-100 rounded-xl px-3 py-2 outline-none bg-white text-slate-600 placeholder:text-slate-300" />
                  </div>
                ))}
              </div>
            </div>

            {/* review */}
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Day Review</p>
              <textarea value={form.dailyReview} onChange={e => setForm(f => ({ ...f, dailyReview: e.target.value }))}
                placeholder="What went well? What to improve tomorrow?"
                rows={3}
                className="w-full text-xs border border-slate-200 rounded-2xl px-4 py-3 outline-none resize-none bg-slate-50 text-slate-600 placeholder:text-slate-300" />
            </div>

            <button onClick={saveLog} disabled={saving || form.sessions.length === 0}
              className="w-full py-3.5 rounded-2xl text-white font-bold cursor-pointer border-none disabled:opacity-40 text-sm"
              style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}>
              {saving ? 'Saving…' : 'Save Log'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════════════════ */
export default function PrepHubPage() {
  const { user } = useAuth();
  const [tab, setTab]         = useState('notes');
  const [streak, setStreak]   = useState({ streak: 0, totalDays: 0, totalHours: 0, avgHours: 0 });
  const [logs, setLogs]       = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const past90 = new Date(); past90.setDate(past90.getDate() - 90);
    Promise.all([
      prepApi.getLogs({ from: past90.toISOString().slice(0, 10) }),
      prepApi.getStreak(),
    ]).then(([l, s]) => { setLogs(l); setStreak(s); }).catch(() => {}).finally(() => setDataLoading(false));
  }, []);

  // Current week hours (Mon–Sun containing today)
  const thisWeekHours = (() => {
    const base = new Date(); const dow = base.getDay();
    const monday = new Date(base); monday.setDate(base.getDate() - ((dow + 6) % 7));
    let total = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday); d.setDate(monday.getDate() + i);
      const ds = d.toISOString().slice(0, 10);
      total += logs.find(l => l.dateStr === ds)?.totalHours || 0;
    }
    return total;
  })();

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center text-slate-400">
          <NotebookPen size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold">Sign in to access Prep Hub</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* hero header */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#0F172A 0%,#1E1B4B 60%,#312E81 100%)' }}>
        {/* decorative circles */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#818CF8,transparent)' }} />
        <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full opacity-10" style={{ background: 'radial-gradient(circle,#C4B5FD,transparent)' }} />

        <div className="w-full px-6 pt-8 pb-6 relative">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#6366F1,#8B5CF6)' }}>
              <NotebookPen size={16} color="#fff" />
            </div>
            <h1 className="font-extrabold text-xl text-white tracking-tight">Prep Hub</h1>
            <Sparkles size={14} className="text-indigo-300 ml-auto" />
          </div>
          <p className="text-sm text-indigo-300 mb-5">Your personal study space</p>

          {/* mini stats */}
          <div className="flex gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <span className="text-xs font-extrabold" style={{ color: '#FB923C' }}>
                {dataLoading ? '—' : `${streak.streak}d`}
              </span>
              <span className="text-[10px] text-white/40">Streak</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <span className="text-xs font-extrabold" style={{ color: '#A5B4FC' }}>
                {dataLoading ? '—' : `${thisWeekHours.toFixed(1)}h`}
              </span>
              <span className="text-[10px] text-white/40">This week</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <span className="text-xs font-extrabold" style={{ color: '#6EE7B7' }}>
                {dataLoading ? '—' : `${Number(streak.avgHours).toFixed(1)}h`}
              </span>
              <span className="text-[10px] text-white/40">Avg / day</span>
            </div>
          </div>
        </div>

        {/* tab bar — sits at bottom of header */}
        <div className="w-full px-6 pb-0">
          <div className="flex gap-1 border-t border-white/10 pt-1">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-bold cursor-pointer border-none transition-all relative"
                style={{ background: 'transparent', color: tab === id ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                <Icon size={14} />
                {label}
                {tab === id && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full" style={{ background: 'linear-gradient(90deg,#6366F1,#8B5CF6)' }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* content */}
      <div className="w-full px-6 pt-5">
        {tab === 'notes'     && <NotesTab />}
        {tab === 'timetable' && <TimetableTab />}
        {tab === 'tracker'   && <TrackerTab logs={logs} setLogs={setLogs} streak={streak} setStreak={setStreak} loading={dataLoading} />}
      </div>
    </div>
  );
}
